/**
 * Turn stored resume URLs (often private S3) into API gateway proxy URLs
 * served by user-service GET /resumes/{path}. Downloads must send a JWT.
 */
import axios from 'axios';
import { resumeFileNameFromUrl, triggerBlobDownload } from './downloadFile';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export function getProxiedResumeUrl(fileUrl) {
  if (!fileUrl) return null;

  if (fileUrl.startsWith(`${API_BASE_URL}/resumes/`)) {
    return fileUrl;
  }

  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    try {
      const url = new URL(fileUrl);
      let s3Key = url.pathname;
      if (s3Key.startsWith('/')) {
        s3Key = s3Key.substring(1);
      }
      s3Key = s3Key.split('?')[0];

      if (s3Key && s3Key.startsWith('resumes/')) {
        return `${API_BASE_URL}/${s3Key}`;
      }

      const resumesIndex = s3Key.indexOf('resumes/');
      if (resumesIndex !== -1) {
        return `${API_BASE_URL}/${s3Key.substring(resumesIndex)}`;
      }
    } catch (e) {
      console.error('Error parsing resume URL:', e);
    }
    return fileUrl;
  }

  if (fileUrl.startsWith('/resumes/')) {
    return `${API_BASE_URL}${fileUrl}`;
  }

  if (fileUrl.startsWith('/uploads/resumes/')) {
    const relative = fileUrl.replace(/^\/uploads\//, '');
    return `${API_BASE_URL}/${relative}`;
  }

  if (fileUrl.startsWith('/')) {
    return `${API_BASE_URL}${fileUrl}`;
  }

  return fileUrl;
}

export function isGatewayResumeUrl(fileUrl) {
  if (!fileUrl) return false;
  if (fileUrl.startsWith('/')) return true;
  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(
    /\/$/,
    ''
  );
  return (
    fileUrl.startsWith(`${apiBase}/resumes/`) ||
    fileUrl.startsWith(`${apiBase}/uploads/`)
  );
}

/** Authenticated blob download. Raw <a href> cannot send Authorization. */
export async function downloadResumeAuthenticated(fileUrl, filename) {
  const url = getProxiedResumeUrl(fileUrl);
  if (!url) {
    throw new Error('No resume URL');
  }
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not authenticated');
  }
  const response = await axios.get(url, {
    responseType: 'blob',
    headers: { Authorization: `Bearer ${token}` },
  });
  triggerBlobDownload(response.data, filename || resumeFileNameFromUrl(fileUrl));
}
